// src/pages/DeleteAccount.tsx
import React from "react";
import { Link } from "react-router-dom";
import {
  MdDeleteForever,
  MdOutlinePrivacyTip,
  MdAlternateEmail,
  MdSecurity,
  MdGavel,
} from "react-icons/md";

export default function DeleteAccount(): React.ReactElement {
  return (
    <main className="min-h-screen bg-app text-onSurface">
      <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 py-10 md:px-6 lg:px-8">
        {/* HERO */}
        <section className="rounded-2xl border border-surface/40 bg-surface/60 p-6 shadow-sm backdrop-blur-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-error/10">
                <MdDeleteForever className="h-7 w-7 text-error" />
              </div>
              <div>
                <h1 className="text-xl font-semibold md:text-2xl">
                  Eliminar cuenta y datos de RADAR 4
                </h1>
                <p className="mt-1 text-sm opacity-80">
                  En esta página te explicamos cómo solicitar la eliminación de
                  tu cuenta y de los datos asociados a tu uso de la aplicación
                  móvil RADAR 4 (iOS y Android).
                </p>
                <p className="mt-2 text-xs uppercase tracking-wide opacity-60">
                  Última actualización: 1 de diciembre de 2025
                </p>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-2 md:mt-0 md:justify-end">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                <MdOutlinePrivacyTip className="h-4 w-4" />
                Control de tus datos
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-error/10 px-3 py-1 text-xs font-medium text-error">
                <MdDeleteForever className="h-4 w-4" />
                Eliminación de cuenta
              </span>
            </div>
          </div>

          {/* <p className="mt-4 text-xs leading-relaxed opacity-70">
            Esta página sirve como recurso web para las tiendas de aplicaciones
            (Google Play y App Store) y para personas usuarias que desean
            gestionar la eliminación de su cuenta y datos sin necesidad de tener
            la app instalada.
          </p> */}
        </section>

        {/* 1. ENLACE CON LA APP */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdOutlinePrivacyTip className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              1. Eliminación de cuenta desde la app RADAR 4
            </h2>
          </div>
          <p className="text-sm leading-relaxed opacity-90">
            Si en tu versión de RADAR 4 ya está disponible la opción de
            eliminación dentro de la app, podrás iniciar el proceso desde el
            menú de <strong>Perfil / Cuenta</strong>, en el apartado de
            <strong> configuración de privacidad</strong> (por ejemplo:
            “Eliminar cuenta” o similar).
          </p>
          <p className="mt-2 text-xs leading-relaxed opacity-70">
            Esta opción dentro de la app está pensada para cumplir las
            exigencias de Apple y Google de ofrecer un camino sencillo para
            iniciar la eliminación de la cuenta desde la propia aplicación. Si
            todavía no ves esa opción en tu menú, puedes usar el procedimiento
            por correo descrito a continuación.
          </p>
        </section>

        {/* 2. SOLICITUD POR CORREO: PASO A PASO */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdAlternateEmail className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              2. Cómo solicitar la eliminación por correo electrónico
            </h2>
          </div>
          <p className="text-sm leading-relaxed opacity-90">
            Si ya no tienes la app instalada o prefieres hacer la solicitud por
            correo, puedes pedir la eliminación de tu cuenta y datos siguiendo
            estos pasos:
          </p>
          <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm opacity-90">
            <li>
              Abre tu cliente de correo electrónico y redacta un mensaje a:
              <br />
              <a href="mailto:radar4sostenibilidad@gmail.com" className="link">
                radar4sostenibilidad@gmail.com
              </a>
            </li>
            <li>
              En el <strong>asunto</strong> del correo escribe:
              <br />
              <span className="rounded bg-surfaceVariant px-2 py-1 text-xs font-mono">
                Solicitud de eliminación de cuenta RADAR 4
              </span>
            </li>
            <li>
              En el <strong>cuerpo del mensaje</strong>, incluye al menos la
              siguiente información:
              <ul className="mt-1 list-disc space-y-1 pl-5">
                <li>
                  Tu <strong>nombre completo</strong>.
                </li>
                <li>
                  La <strong>dirección de correo electrónico</strong> con la que
                  te registraste en la app RADAR 4.
                </li>
                <li>
                  La <strong>organización o proyecto</strong> con el que estás
                  vinculada/o en RADAR 4 (por ejemplo, empresa, universidad,
                  administración pública).
                </li>
                <li>
                  Una breve indicación de si deseas:
                  <ul className="mt-1 list-disc space-y-0.5 pl-5">
                    <li>Eliminar tu cuenta de acceso a la app.</li>
                    <li>
                      Eliminar, en la medida de lo posible, los datos personales
                      asociados a tu cuenta.
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              Envía el correo desde la misma dirección con la que te
              registraste, siempre que sea posible. Esto nos ayuda a verificar
              que la solicitud proviene realmente de la persona titular de la
              cuenta.
            </li>
          </ol>
          <p className="mt-3 text-xs leading-relaxed opacity-70">
            Una vez recibida la solicitud, se revisará y se coordinará con la
            organización que actúe como Operador del proyecto RADAR 4 en el que
            participas, para aplicar la eliminación de cuenta y de datos
            personales en la medida en que sea legalmente posible.
          </p>
        </section>

        {/* 3. QUÉ SE ELIMINA Y QUÉ PUEDE CONSERVARSE */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdSecurity className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              3. Qué ocurre cuando se elimina tu cuenta
            </h2>
          </div>
          <p className="text-sm leading-relaxed opacity-90">
            Cuando se tramita una solicitud de eliminación de cuenta, el
            objetivo es borrar o anonimizar los datos personales asociados a tu
            perfil en RADAR 4, de acuerdo con la política de privacidad y la
            legislación aplicable.
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm opacity-90">
            <li>
              Tu <strong>cuenta de acceso</strong> se desactiva o elimina y ya
              no podrás iniciar sesión en la app con esas credenciales.
            </li>
            <li>
              Se eliminan o anonimizan, en la medida de lo posible, los datos
              personales directamente vinculados a tu perfil (por ejemplo,
              nombre y correo de usuario), salvo que exista una obligación legal
              de conservarlos durante más tiempo.
            </li>
            <li>
              Ciertos datos vinculados a evaluaciones o reportes pueden
              conservarse de forma anonimizada o bajo responsabilidad del
              Operador, cuando sean necesarios para mantener la trazabilidad de
              resultados, cumplir requisitos de auditoría o justificar
              certificaciones.
            </li>
          </ul>
          <p className="mt-3 text-xs leading-relaxed opacity-70">
            Tal y como permiten las políticas de Google Play y de la App Store,
            es posible que determinados datos deban conservarse durante un
            periodo adicional por motivos legales, de seguridad, de prevención
            de fraude o de defensa ante reclamaciones. En esos casos, la
            información se mantendrá protegida y con acceso restringido.
          </p>
        </section>

        {/* 4. DUDAS, ACCESO Y RECTIFICACIÓN */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdGavel className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              4. Otras solicitudes sobre tus datos (acceso, rectificación, etc.)
            </h2>
          </div>
          <p className="text-sm leading-relaxed opacity-90">
            Además de la eliminación, las leyes de protección de datos pueden
            reconocerte otros derechos, como el acceso a tus datos, la
            rectificación de información inexacta o la limitación del
            tratamiento en determinadas circunstancias.
          </p>
          <p className="mt-2 text-sm leading-relaxed opacity-90">
            Puedes iniciar estas solicitudes utilizando el mismo correo de
            contacto:
          </p>
          <p className="mt-1 text-sm">
            <a
              href="mailto:radar4sostenibilidad@gmail.com"
              className="link font-medium"
            >
              radar4sostenibilidad@gmail.com
            </a>
          </p>
          <p className="mt-3 text-xs leading-relaxed opacity-70">
            En proyectos donde exista una organización Operadora (por ejemplo,
            universidad, empresa o administración pública), también puedes
            acudir a los canales de privacidad o cumplimiento que dicha entidad
            haya comunicado oficialmente.
          </p>
        </section>

        {/* FOOTER */}
        <section className="rounded-2xl border border-surface/30 bg-surface/60 p-5 text-xs leading-relaxed opacity-70">
          <p>
            Este texto es un recurso informativo general y debe ser revisado y
            adaptado por el equipo legal responsable del proyecto RADAR 4, para
            alinearse con la normativa específica aplicable en tu país o región.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/privacy" className="link">
              Ver política de privacidad completa
            </Link>
            <span className="opacity-40">•</span>
            <Link to="/" className="link">
              Volver al inicio
            </Link>
            <span className="opacity-40">•</span>
            <Link to="/login" className="link">
              Ir a la página de acceso
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
